import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTheme } from '@mui/material/styles';
import GrantExpiryChip from '../../../admin/components/GrantExpiryChip';
import PermissionGate from '../../../admin/components/PermissionGate';
import { safeJsonParse } from '../../../admin/api';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import { fDateTime } from '../../../utils/formatTime';
import {
  AdminActionBar,
  AdminDetailPanel,
  AdminNoDataState,
  AdminStatusChip,
} from '../shared';
import { RequestState } from '../../../pages/admin/AdminRoutePageShared';
import { PROFILE_TABS, WORK_DAYS } from './accessControlUtils';

function StaffProfileOverview({ detail }) {
  const profile = detail?.profile;
  const workDays = safeJsonParse(profile?.work_days_json, []) || [];

  return (
    <Stack spacing={3}>
      <AdminDetailPanel
        title="Governed Profile"
        items={[
          // { label: 'Staff ID', value: profile?.staff_id || 'N/A' },
          { label: 'Role Code', value: profile?.role_code || 'N/A' },
          { label: 'Timezone', value: profile?.timezone || 'N/A' },
          { label: 'Status', value: profile?.status || 'N/A' },
          { label: 'Shift Start', value: profile?.shift_start_time || 'N/A' },
          { label: 'Shift End', value: profile?.shift_end_time || 'N/A' },
          { label: 'After Hours', value: profile?.allow_after_hours ? 'Allowed' : 'Blocked' },
          { label: 'Updated At', value: profile?.updated_at ? fDateTime(profile.updated_at) : 'N/A' },
        ]}
      />

      <Card variant="outlined">
        <Box sx={{ p: 3 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">Work Days</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {workDays.length ? (
                workDays.map((day) => <Chip key={day} label={day} size="small" color="primary" variant="outlined" />)
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No governed work days returned for this staff profile.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}

StaffProfileOverview.propTypes = {
  detail: PropTypes.shape({
    profile: PropTypes.object,
  }),
};

StaffProfileOverview.defaultProps = {
  detail: null,
};

function SessionsAndGrantsView({ detail, onRequestAccess }) {
  const sessions = detail?.sessions || [];
  const grants = detail?.grants || [];

  return (
    <Stack spacing={3}>
      <AdminActionBar
        title="Elevated Access"
        description="Review active session context and currently issued grants for the selected staff profile."
        actions={[
          {
            label: 'Request Access',
            icon: 'solar:key-minimalistic-square-3-bold',
            variant: 'contained',
          },
        ]}
        onActionClick={onRequestAccess}
      />

      <Card variant="outlined">
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1">Sessions</Typography>
            {sessions.length ? (
              sessions.map((session) => (
                <Card key={session.id} variant="outlined">
                  <Box sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">{session.session_id}</Typography>
                        <Chip label={session.trust_level || 'unknown'} size="small" color="info" variant="outlined" />
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">IP Address</Typography>
                          <Typography variant="body2">{session.ip_address || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Environment</Typography>
                          <Typography variant="body2">{session.environment_verdict || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Step-Up Verified</Typography>
                          <Typography variant="body2">{session.step_up_verified_at ? fDateTime(session.step_up_verified_at) : 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Expires At</Typography>
                          <Typography variant="body2">{session.expires_at ? fDateTime(session.expires_at) : 'N/A'}</Typography>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Box>
                </Card>
              ))
            ) : (
              <AdminNoDataState
                title="No sessions"
                description="The governed staff detail payload did not return any active or historical admin sessions."
              />
            )}
          </Stack>
        </Box>
      </Card>

      <Card variant="outlined">
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1">Active Grants</Typography>
            {grants.length ? (
              grants.map((grant) => (
                <Card key={grant.id} variant="outlined">
                  <Box sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle2">{grant.permission_code}</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <AdminStatusChip status={grant.status} />
                          <GrantExpiryChip expiresAt={grant.expires_at} />
                        </Stack>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Resource Type</Typography>
                          <Typography variant="body2">{grant.resource_type || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Resource ID</Typography>
                          <Typography variant="body2">{grant.resource_id || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Approved By</Typography>
                          <Typography variant="body2">{grant.approved_by || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Starts / Expires</Typography>
                          <Typography variant="body2">
                            {grant.starts_at ? fDateTime(grant.starts_at) : 'N/A'} / {grant.expires_at ? fDateTime(grant.expires_at) : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Reason</Typography>
                          <Typography variant="body2">{grant.reason || 'N/A'}</Typography>
                        </Grid>
                      </Grid>
                    </Stack>
                  </Box>
                </Card>
              ))
            ) : (
              <AdminNoDataState
                title="No grants"
                description="The governed staff detail payload did not return any elevated grants for this staff member."
              />
            )}
          </Stack>
        </Box>
      </Card>
    </Stack>
  );
}

SessionsAndGrantsView.propTypes = {
  detail: PropTypes.shape({
    grants: PropTypes.array,
    sessions: PropTypes.array,
  }),
  onRequestAccess: PropTypes.func.isRequired,
};

SessionsAndGrantsView.defaultProps = {
  detail: null,
};

export default function AccessControlStaffDrawer({
  open,
  selectedAdmin,
  selectedStaffId,
  drawerTab,
  onTabChange,
  onClose,
  detailState,
  roleOptions,
  permissionOptions,
  profileDialogOpen,
  onProfileDialogClose,
  onOpenProfileDialog,
  requestDialogOpen,
  onRequestDialogClose,
  onOpenRequestDialog,
  profileForm,
  setProfileForm,
  profileErrors,
  requestForm,
  setRequestForm,
  requestErrors,
  submittingProfile,
  submittingRequest,
  onSaveWorkProfile,
  onSubmitAccessRequest,
}) {
  const theme = useTheme();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560, lg: 640 },
            p: 3,
          },
        }}
      >
        {!selectedAdmin ? (
          <AdminNoDataState
            title="Select a staff member"
            description="Open an admin row to load the governed staff profile, session context, and grant detail in this drawer."
          />
        ) : (
          <Stack spacing={3} sx={{ height: '100%' }}>
            <Stack spacing={2.5}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar src={selectedAdmin.image_url} alt={selectedAdmin.first_name} sx={{ width: 56, height: 56 }} />
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6">
                        {selectedAdmin.first_name} {selectedAdmin.last_name}
                      </Typography>
                      {selectedAdmin.is_admin ? (
                        <Label variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'} color="secondary">
                          Admin
                        </Label>
                      ) : null}
                    </Stack>
                    {/* <Typography variant="body2" color="text.secondary">
                      {selectedStaffId || 'No staff id'}
                    </Typography> */}
                    <Typography variant="body2" color="text.secondary">
                      {selectedAdmin.email || 'No email returned by admin resource'}
                    </Typography>
                  </Stack>
                </Stack>
                <IconButton onClick={onClose} aria-label="Close access control drawer">
                  <Iconify icon="eva:close-fill" />
                </IconButton>
              </Stack>

              <AdminActionBar
                title="Staff Actions"
                description=""
                actions={[
                  {
                    label: 'Edit Work Profile',
                    icon: 'solar:pen-bold',
                    variant: 'contained',
                  },
                  {
                    label: 'Request Access',
                    icon: 'solar:key-minimalistic-square-3-bold',
                  },
                ]}
                onActionClick={(action) => {
                  if (action.label === 'Edit Work Profile') {
                    onOpenProfileDialog();
                    return;
                  }

                  if (action.label === 'Request Access') {
                    onOpenRequestDialog();
                  }
                }}
              />
            </Stack>

            <Tabs value={drawerTab} onChange={(_, nextValue) => onTabChange(nextValue)} variant="fullWidth">
              {PROFILE_TABS.map((tab) => (
                <Tab key={tab.value} value={tab.value} label={tab.label} />
              ))}
            </Tabs>

            <Divider />

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
              <RequestState loading={detailState.loading} error={detailState.error}>
                {drawerTab === 'profile' ? (
                  <StaffProfileOverview detail={detailState.data} />
                ) : (
                  <SessionsAndGrantsView detail={detailState.data} onRequestAccess={onOpenRequestDialog} />
                )}
              </RequestState>
            </Box>
          </Stack>
        )}
      </Drawer>

      <Dialog open={profileDialogOpen} onClose={onProfileDialogClose} fullWidth maxWidth="md">
        <DialogTitle>Edit Work Profile</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* <TextField label="Staff ID" value={selectedStaffId} InputProps={{ readOnly: true }} /> */}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Role"
                  value={profileForm.role_code}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, role_code: event.target.value }))}
                  error={Boolean(profileErrors.role_code)}
                  helperText={profileErrors.role_code}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role.id} value={role.role_code}>
                      {role.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Timezone"
                  value={profileForm.timezone}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, timezone: event.target.value }))}
                  error={Boolean(profileErrors.timezone)}
                  helperText={profileErrors.timezone || 'Enter the backend-supported timezone string.'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(profileErrors.work_days)}>
                  <InputLabel id="work-days-label">Work Days</InputLabel>
                  <Select
                    labelId="work-days-label"
                    multiple
                    value={profileForm.work_days}
                    label="Work Days"
                    renderValue={(selectedValues) => selectedValues.join(', ')}
                    onChange={(event) => setProfileForm((prev) => ({ ...prev, work_days: event.target.value }))}
                  >
                    {WORK_DAYS.map((day) => (
                      <MenuItem key={day} value={day}>
                        <Checkbox checked={profileForm.work_days.includes(day)} />
                        <ListItemText primary={day} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{profileErrors.work_days}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={profileForm.status}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, status: event.target.value }))}
                  error={Boolean(profileErrors.status)}
                  helperText={profileErrors.status}
                >
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="inactive">inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TimePicker
                  label="Shift Start Time"
                  value={profileForm.shift_start_time}
                  onChange={(value) => setProfileForm((prev) => ({ ...prev, shift_start_time: value }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={Boolean(profileErrors.shift_start_time)}
                      helperText={profileErrors.shift_start_time}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TimePicker
                  label="Shift End Time"
                  value={profileForm.shift_end_time}
                  onChange={(value) => setProfileForm((prev) => ({ ...prev, shift_end_time: value }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={Boolean(profileErrors.shift_end_time)}
                      helperText={profileErrors.shift_end_time}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Allow After Hours"
                  value={String(profileForm.allow_after_hours)}
                  onChange={(event) => setProfileForm((prev) => ({ ...prev, allow_after_hours: event.target.value === 'true' }))}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onProfileDialogClose}>Cancel</Button>
          <PermissionGate moduleName="access" permissionCode="access.manage">
            <LoadingButton loading={submittingProfile} variant="contained" onClick={onSaveWorkProfile}>
              Save Work Profile
            </LoadingButton>
          </PermissionGate>
        </DialogActions>
      </Dialog>

      <Dialog open={requestDialogOpen} onClose={onRequestDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Request Elevated Access</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* <TextField label="Staff ID" value={selectedStaffId} InputProps={{ readOnly: true }} /> */}
            <TextField
              select
              fullWidth
              label="Permission"
              value={requestForm.permission_code}
              onChange={(event) => setRequestForm((prev) => ({ ...prev, permission_code: event.target.value }))}
              error={Boolean(requestErrors.permission_code)}
              helperText={requestErrors.permission_code}
            >
              {permissionOptions.map((permission) => (
                <MenuItem key={permission.id} value={permission.permission_code}>
                  {permission.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Resource Type"
              value={requestForm.resource_type}
              onChange={(event) => setRequestForm((prev) => ({ ...prev, resource_type: event.target.value }))}
              error={Boolean(requestErrors.resource_type)}
              helperText={requestErrors.resource_type || 'Kept as text until the backend contract publishes the enum list.'}
            />
            <TextField
              fullWidth
              label="Resource ID"
              value={requestForm.resource_id}
              onChange={(event) => setRequestForm((prev) => ({ ...prev, resource_id: event.target.value }))}
              error={Boolean(requestErrors.resource_id)}
              helperText={requestErrors.resource_id}
            />
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Reason"
              value={requestForm.reason}
              onChange={(event) => setRequestForm((prev) => ({ ...prev, reason: event.target.value }))}
              error={Boolean(requestErrors.reason)}
              helperText={requestErrors.reason}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onRequestDialogClose}>Cancel</Button>
          <LoadingButton loading={submittingRequest} variant="contained" onClick={onSubmitAccessRequest}>
            Submit Request
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

AccessControlStaffDrawer.propTypes = {
  detailState: PropTypes.shape({
    data: PropTypes.object,
    error: PropTypes.string,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  drawerTab: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenProfileDialog: PropTypes.func.isRequired,
  onOpenRequestDialog: PropTypes.func.isRequired,
  onProfileDialogClose: PropTypes.func.isRequired,
  onRequestDialogClose: PropTypes.func.isRequired,
  onSaveWorkProfile: PropTypes.func.isRequired,
  onSubmitAccessRequest: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  permissionOptions: PropTypes.arrayOf(PropTypes.object),
  profileDialogOpen: PropTypes.bool.isRequired,
  profileErrors: PropTypes.object.isRequired,
  profileForm: PropTypes.object.isRequired,
  requestDialogOpen: PropTypes.bool.isRequired,
  requestErrors: PropTypes.object.isRequired,
  requestForm: PropTypes.object.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  selectedAdmin: PropTypes.object,
  selectedStaffId: PropTypes.string.isRequired,
  setProfileForm: PropTypes.func.isRequired,
  setRequestForm: PropTypes.func.isRequired,
  submittingProfile: PropTypes.bool.isRequired,
  submittingRequest: PropTypes.bool.isRequired,
};

AccessControlStaffDrawer.defaultProps = {
  permissionOptions: [],
  roleOptions: [],
  selectedAdmin: null,
};
